import classNames from 'classnames';
import { Parser } from 'html-to-react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useTranslation } from 'react-i18next';
import { ACTION_VARIANTS, Button, ICON_NAMES } from '@reverse-hr/design-system';
import { SummaryDetails } from '@components/Organisms/SummaryDetails/index';
import { CurriculumPreview } from '@components/Curriculum/_partials/CurriculumPreview';
import { CurriculumModal } from '@components/_modals/CurriculumModal/index';
import { curriculumUrlSelector } from '@model/Candidate/Candidate.selectors';
import { selectorRootActions, selectorRootState } from '@model/index';
import { MODAL_IDS_MAP } from '@app/constants/modals';
import { formatLinebreak } from '@utils/functions';
import type { FC } from 'react';
import type { TCurriculumProps } from '@components/Curriculum/Curriculum.types';

const HTML_PARSER = new Parser();

export const Curriculum: FC<TCurriculumProps> = props => {
  const { className, summary } = props;

  const { t } = useTranslation('', {
    keyPrefix: 'candidateDetails.dossier',
  });

  const curriculumUrl = useStoreState(curriculumUrlSelector);
  const { isModalOpened, modalId } = useStoreState(selectorRootState);
  const { setModalId, setModalOpened } = useStoreActions(selectorRootActions);

  const isCurriculumAvailable = !!curriculumUrl;

  const isCurriculumModalVisible =
    isModalOpened && modalId === MODAL_IDS_MAP.CURRICULUM;

  const wrapperClassNames = classNames('app-c-curriculum', className);

  const onShowCurriculumClick = (): void => {
    setModalId(MODAL_IDS_MAP.CURRICULUM);
    setModalOpened(true);
  };

  const onCurriculumModalClose = (): void => {
    setModalOpened(false);
    setModalId(null);
  };

  return (
    <>
      <div className={wrapperClassNames}>
        <div className="app-c-curriculum__recap">
          <div className="app-c-curriculum__screening">
            <div className="app-c-curriculum__screening-text">
              {HTML_PARSER.parse(formatLinebreak(summary))}
            </div>

            {isCurriculumAvailable && (
              <Button
                className="app-c-curriculum__show-curriculum"
                isFullWidth
                leftIcon={ICON_NAMES.SHOW}
                onClick={onShowCurriculumClick}
                variant={ACTION_VARIANTS.OUTLINE}
              >
                {t('showCurriculumButton')}
              </Button>
            )}

            <SummaryDetails />
          </div>
        </div>

        {isCurriculumAvailable && (
          <CurriculumPreview
            curriculumUrl={curriculumUrl}
            onClick={onShowCurriculumClick}
          />
        )}
      </div>

      {isCurriculumModalVisible && (
        <CurriculumModal onClose={onCurriculumModalClose} />
      )}
    </>
  );
};
